import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';

export const settingsService = {
  /**
   * Fetch all site settings as a consolidated dictionary
   */
  async getAll() {
    if (!isSupabaseConfigured) return {};
    const { data, error } = await supabase
      .from('site_settings')
      .select('setting_key, setting_value');
    if (error) {
      console.warn('Settings fetch warning:', error.message);
      return {};
    }

    const settingsMap = {};
    (data || []).forEach((row) => {
      settingsMap[row.setting_key] = row.setting_value;
    });

    // Also flatten grouped objects for easy access
    const general = typeof settingsMap.general === 'object' && settingsMap.general !== null ? settingsMap.general : {};
    const hero = typeof settingsMap.hero === 'object' && settingsMap.hero !== null ? settingsMap.hero : {};
    const seo = typeof settingsMap.seo === 'object' && settingsMap.seo !== null ? settingsMap.seo : {};

    return {
      ...general,
      ...hero,
      ...seo,
      ...settingsMap,
      general: {
        ...general,
        business_name: settingsMap.business_name || general.business_name,
        tagline: settingsMap.tagline || general.tagline,
        phone: settingsMap.phone || general.phone,
        phone_raw: settingsMap.phone ? settingsMap.phone.replace(/[^0-9]/g, '') : general.phone_raw,
        email: settingsMap.email || general.email,
        address: settingsMap.address || general.address,
        instagram_url: settingsMap.instagram_url || general.instagram_url,
        facebook_url: settingsMap.facebook_url || general.facebook_url,
        whatsapp_number: settingsMap.whatsapp_number || general.whatsapp,
      },
      hero: {
        ...hero,
        headline: settingsMap.hero_headline || hero.title || hero.headline,
        description: settingsMap.hero_description || hero.subtitle || hero.description,
      },
      seo: {
        ...seo,
        meta_title: settingsMap.seo_title || seo.meta_title,
        meta_description: settingsMap.seo_description || seo.meta_description,
      },
    };
  },

  /**
   * Get a single setting value by key with optional fallback
   */
  async getSetting(key, defaultValue = null) {
    if (!isSupabaseConfigured) return defaultValue;
    const { data, error } = await supabase
      .from('site_settings')
      .select('setting_value')
      .eq('setting_key', key)
      .maybeSingle();

    if (error || !data || data.setting_value === undefined || data.setting_value === null) {
      // If asking for 'general' or other group, check all settings
      if (key === 'general' || key === 'hero' || key === 'seo') {
        const all = await this.getAll();
        return all[key] || defaultValue;
      }
      return defaultValue;
    }

    if (key === 'general' && typeof data.setting_value === 'object') {
      const all = await this.getAll();
      return {
        ...data.setting_value,
        business_name: all.business_name || data.setting_value.business_name,
        phone: all.phone || data.setting_value.phone,
        email: all.email || data.setting_value.email,
        address: all.address || data.setting_value.address,
        instagram_url: all.instagram_url || data.setting_value.instagram_url,
        facebook_url: all.facebook_url || data.setting_value.facebook_url,
      };
    }

    return data.setting_value;
  },

  /**
   * Upsert a setting key-value pair in Supabase
   */
  async updateSetting(key, value) {
    if (!isSupabaseConfigured) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('site_settings')
      .upsert({ setting_key: key, setting_value: value }, { onConflict: 'setting_key' })
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

export default settingsService;
